import { Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import ArticleCard from '@/components/feature/ArticleCard';
import Breadcrumb from '@/components/base/Breadcrumb';

const featuredArticles = articles.filter((a) => a.featured);
const regularArticles = articles.filter((a) => !a.featured);

export default function Articles() {
  return (
    <div className="min-h-screen">
      {/* Page Header */}
      <section className="relative py-12 md:py-16 bg-background-100 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Minimalist%20editorial%20workspace%20with%20Asian%20magazines%20and%20books%2C%20warm%20cream%20background%2C%20soft%20natural%20lighting%2C%20elegant%20reading%20atmosphere%2C%20cultural%20lifestyle%20photography&width=1800&height=400&seq=articles-header&orientation=landscape"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '精选文章' }]} />
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-2">精选文章</h1>
          <p className="mt-2 text-sm text-foreground-500">深度探索亚洲品牌故事、设计美学与生活方式</p>
        </div>
      </section>

      {/* Featured Articles */}
      {featuredArticles.length > 0 && (
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl font-semibold text-foreground-900 mb-6 flex items-center gap-2">
              <span className="w-1 h-5 bg-accent-500 rounded-full"></span>
              编辑推荐
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {featuredArticles.map((article) => (
                <ArticleCard key={article.id} article={article} variant="featured" />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* All Articles */}
      <section className="py-10 md:py-14 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="font-heading text-xl font-semibold text-foreground-900 mb-6 flex items-center gap-2">
            <span className="w-1 h-5 bg-primary-500 rounded-full"></span>
            全部文章
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {regularArticles.map((article) => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </div>

          {/* Compact list for extra articles */}
          {regularArticles.length > 0 && (
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
              {articles.slice(-4).map((article) => (
                <ArticleCard key={`compact-${article.id}`} article={article} variant="compact" />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Article FAQ */}
      <section className="py-10 md:py-14 bg-background-50">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <h2 className="font-heading text-xl font-semibold text-foreground-900 text-center mb-8">常见问题</h2>
          <div className="space-y-4">
            {[
              { q: '亚洲精品的选品标准是什么？', a: '我们从品质、设计感和文化价值三个维度严格筛选每一个品牌和产品，确保每一件亚洲精品都代表着亚洲制造的顶尖水准。' },
              { q: '如何订阅亚洲精品的文章更新？', a: '您可以在首页底部订阅我们的资讯邮件，或关注我们的社交媒体账号，第一时间获取最新文章和品牌推荐。' },
              { q: '文章中的产品在哪里可以购买？', a: '文章中推荐的亚洲精品均可在我们的分类页面中查看和购买，点击产品即可看到详细信息和购买选项。' },
              { q: '可以投稿或合作吗？', a: '我们欢迎优质内容创作者和品牌方与我们合作。请通过"关于我们"页面的联系方式与我们取得联系。' },
            ].map((faq, i) => (
              <details key={i} className="group bg-background-100 rounded-lg p-5 cursor-pointer">
                <summary className="flex items-center justify-between font-medium text-foreground-900 text-sm">
                  {faq.q}
                  <i className="ri-arrow-down-s-line text-lg text-foreground-400 group-open:rotate-180 transition-transform"></i>
                </summary>
                <p className="mt-3 text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
              </details>
            ))}
          </div>
          <script type="application/ld+json">
            {JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: [
                { '@type': 'Question', name: '亚洲精品的选品标准是什么？', acceptedAnswer: { '@type': 'Answer', text: '我们从品质、设计感和文化价值三个维度严格筛选每一个品牌和产品，确保每一件亚洲精品都代表着亚洲制造的顶尖水准。' } },
                { '@type': 'Question', name: '如何订阅亚洲精品的文章更新？', acceptedAnswer: { '@type': 'Answer', text: '您可以在首页底部订阅我们的资讯邮件，或关注我们的社交媒体账号，第一时间获取最新文章和品牌推荐。' } },
                { '@type': 'Question', name: '文章中的产品在哪里可以购买？', acceptedAnswer: { '@type': 'Answer', text: '文章中推荐的亚洲精品均可在我们的分类页面中查看和购买，点击产品即可看到详细信息和购买选项。' } },
                { '@type': 'Question', name: '可以投稿或合作吗？', acceptedAnswer: { '@type': 'Answer', text: '我们欢迎优质内容创作者和品牌方与我们合作。请通过"关于我们"页面的联系方式与我们取得联系。' } },
              ],
            })}
          </script>
        </div>
      </section>
    </div>
  );
}